import {
  DEFAULT_PAGE,
  DEFAULT_SIZE,
  DEFAULT_SORT,
  ordersToSortArr,
  PageResponse,
  PageSearchCondition,
} from "../../../types/page";
import {
  dataSearchTestResultWideList,
  getTestResultWideColumnsAsync,
  getTestResultWideListAsync,
  setTestResultWideList,
} from "../../../store/dataSearchSlice";
import { useAppDispatch, useAppSelector, useListFilterValues } from "../../../hooks/hooks";
import { useEffect, useMemo, useState } from "react";
import { DataSearchListQueryType, DataSearchFilterValues } from "./useDataSearchFilterValues";
import { useUtil } from "../../../util/hooks/useUtil";
import { dateToString } from "../../../util/dateUtil";
import { useSortParam } from "../../../hooks/list/useSortParam";
import { DataSearchTestType, TestResultSearchCondition, TestResultWideDataObj } from "../../../types/dataSearch";
import { Gender } from "../../../types/common";

interface Args {
  onCompletedCallback?: () => void;
}

export function useTestResultWideList({ onCompletedCallback }: Args) {
  const dispatch = useAppDispatch();
  const testResultWideList = useAppSelector(dataSearchTestResultWideList);
  const { filterValues, searchList, listQueryType } = useListFilterValues();
  const dataSearchFilterValues = filterValues as DataSearchFilterValues;
  const { defaultErrorMessage } = useUtil();
  const { toSortArray } = useSortParam();

  const pageCondition = useMemo(
    () => ({
      page: testResultWideList?.number ?? DEFAULT_PAGE,
      size: testResultWideList?.size ?? DEFAULT_SIZE,
      sort: ordersToSortArr(testResultWideList?.orders),
    }),
    [testResultWideList]
  );

  const {
    qFilterValue,
    ageYearFilterValue,
    genderFilterValue,
    stateFilterValue,
    cityFilterValue,
    endedAtFromFilterValue,
    endedAtToFilterValue,
    testTypeFilterValue,
    onlyCompletedFilterValue,
    excludeTestAccountFilterValue,
  } = dataSearchFilterValues;

  const [totalCnt, setTotalCnt] = useState<number | null>(null);
  const [columnKeys, setColumnKeys] = useState<string[]>([]);

  const getSearchParam = (): DataSearchListQueryType => ({
    q: qFilterValue.value ? qFilterValue.value.trim() : undefined,
    ageYear: ageYearFilterValue.value ? ageYearFilterValue.value : undefined,
    gender: genderFilterValue.value ? genderFilterValue.value : undefined,
    state: stateFilterValue.value ? stateFilterValue.value.trim() : undefined,
    city: cityFilterValue.value ? cityFilterValue.value.trim() : undefined,
    endedAtFrom: endedAtFromFilterValue.value ? dateToString(endedAtFromFilterValue.value) : undefined,
    endedAtTo: endedAtToFilterValue.value ? dateToString(endedAtToFilterValue.value) : undefined,
    testType: testTypeFilterValue.value ? testTypeFilterValue.value : undefined,
    onlyCompleted: onlyCompletedFilterValue.value ? "true" : undefined,
    excludeTestAccount: excludeTestAccountFilterValue.value ? "true" : undefined,
  });

  const searchTestResultWideList = (pageSearchCondition: PageSearchCondition) => {
    const param = getSearchParam();
    return () => searchList(pageSearchCondition, param)();
  };

  const getTestResultWideColumns = async (condition: TestResultSearchCondition) => {
    try {
      const result: string[] = await dispatch(getTestResultWideColumnsAsync(condition)).unwrap();
      setColumnKeys(result ?? []);
    } catch (e) {
      console.error(e);
    }
  };

  const getTestResultWideList = async (condition: TestResultSearchCondition, isFirstLoad?: boolean) => {
    try {
      const result: PageResponse<TestResultWideDataObj> = await dispatch(
        getTestResultWideListAsync(condition)
      ).unwrap();
      if (isFirstLoad) {
        setTotalCnt(result.totalElements);
      } else {
        dispatch(setTestResultWideList(result));
      }
      await getTestResultWideColumns(condition);
      onCompletedCallback?.();
    } catch (e) {
      console.error(e);
      alert(defaultErrorMessage);
    }
  };

  useEffect(() => {
    getTestResultWideList({}, true).then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (listQueryType) {
      getTestResultWideList({
        pageCondition: {
          page: listQueryType.page ? Number(listQueryType.page) : DEFAULT_PAGE,
          size: listQueryType.size ? Number(listQueryType.size) : DEFAULT_SIZE,
          sort: listQueryType.sort ? toSortArray(listQueryType.sort) : DEFAULT_SORT,
        },
        q: listQueryType.q?.trim(),
        ageYear: listQueryType.ageYear ? Number(listQueryType.ageYear) : undefined,
        gender: listQueryType.gender ? (listQueryType.gender as Gender) : undefined,
        state: listQueryType.state ? listQueryType.state : undefined,
        city: listQueryType.city ? listQueryType.city : undefined,
        endedAtFrom: listQueryType.endedAtFrom ? listQueryType.endedAtFrom : undefined,
        endedAtTo: listQueryType.endedAtTo ? listQueryType.endedAtTo : undefined,
        testType: listQueryType.testType ? (listQueryType.testType as DataSearchTestType) : undefined,
        onlyCompleted: listQueryType.onlyCompleted === "true" ? true : undefined,
        excludeTestAccount: listQueryType.excludeTestAccount === "true" ? true : undefined,
      }).then();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listQueryType]);

  return {
    searchTestResultWideList,
    totalCnt,
    testResultWideList,
    columnKeys,
    pageCondition,
    getSearchParam,
  };
}
